// Minimal fetch wrapper for the Renavora API (ADR 0040 P6-5, plan §2.2).
// Every request is credentialed (httpOnly session cookie, ADR 0040 D9) — the
// client never sees or stores a token. Non-2xx responses become
// ApiRequestError carrying the wire {code, message} plus the parsed body so
// callers can map structured errors (e.g. 409 save_conflict carries
// `current`).

export class ApiRequestError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    /** Parsed JSON error body, when the response had one. */
    readonly body?: unknown,
  ) {
    super(message);
    this.name = 'ApiRequestError';
  }
}

export interface ApiClient {
  get(path: string): Promise<Response>;
  post(path: string, body?: unknown): Promise<Response>;
  put(path: string, body: unknown): Promise<Response>;
  delete(path: string): Promise<Response>;
}

export function createApiClient(baseUrl: string, fetchImpl: typeof fetch = (...args) => fetch(...args)): ApiClient {
  const request = async (method: string, path: string, body?: unknown): Promise<Response> => {
    let response: Response;
    try {
      response = await fetchImpl(`${baseUrl}${path}`, {
        ...(body === undefined ? {} : { body: JSON.stringify(body) }),
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        method,
      });
    } catch {
      throw new ApiRequestError(0, 'network_error', 'Could not reach the server.');
    }
    if (!response.ok) {
      let parsed: unknown;
      try {
        parsed = await response.json();
      } catch {
        parsed = undefined;
      }
      const wire = parsed as { code?: unknown; message?: unknown } | undefined;
      throw new ApiRequestError(
        response.status,
        typeof wire?.code === 'string' ? wire.code : 'http_error',
        typeof wire?.message === 'string' ? wire.message : `Request failed (${response.status})`,
        parsed,
      );
    }
    return response;
  };

  return {
    delete: (path) => request('DELETE', path),
    get: (path) => request('GET', path),
    post: (path, body) => request('POST', path, body),
    put: (path, body) => request('PUT', path, body),
  };
}
